import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Slider } from "../ui/slider";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { toast } from "sonner";
import { 
  TrendingUp, 
  Settings, 
  Save, 
  RefreshCw,
  Flame,
  AlertTriangle,
  Clock,
  Package,
  DollarSign,
  Percent
} from "lucide-react";
import { API } from "../../apiConfig";


const DEFAULT_TIERS = [
  { stock_percent: 100, multiplier: 1.0, label: null },
  { stock_percent: 50, multiplier: 1.15, label: "Selling Fast" },
  { stock_percent: 25, multiplier: 1.30, label: "Low Stock" },
  { stock_percent: 10, multiplier: 1.50, label: "Almost Gone" },
  { stock_percent: 5, multiplier: 1.75, label: "Last Few!" },
];

const DynamicPricingTab = ({ products, token, onRefresh }) => {
  const [settings, setSettings] = useState({
    enabled: true,
    apply_to_categories: ["limited_edition", "original"],
    tiers: DEFAULT_TIERS
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/dynamic-pricing`, { headers });
      setSettings(res.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/dynamic-pricing`, settings, { headers });
      toast.success("Dynamic pricing settings saved!");
      onRefresh?.();
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const updateTier = (index, field, value) => {
    const newTiers = [...settings.tiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    setSettings({ ...settings, tiers: newTiers });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', { 
      style: 'currency', 
      currency: 'AUD' 
    }).format(price || 0);
  };

  const getScarcityIcon = (label) => {
    switch (label) {
      case "Last Few!": return <Flame className="w-4 h-4 text-red-500" />;
      case "Almost Gone": return <AlertTriangle className="w-4 h-4 text-orange-500" />;
      case "Low Stock": return <Clock className="w-4 h-4 text-amber-500" />;
      case "Selling Fast": return <TrendingUp className="w-4 h-4 text-blue-500" />;
      default: return <Package className="w-4 h-4 text-slate-400" />;
    }
  };

  // Filter products with active dynamic pricing
  const productsWithDynamicPricing = products?.filter(p => 
    p.dynamic_pricing?.dynamic_pricing_active
  ) || [];

  return (
    <div className="space-y-6" data-testid="dynamic-pricing-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20">
            <TrendingUp className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold">Dynamic Pricing</h2>
            <p className="text-muted-foreground">Automatically adjust prices based on stock levels</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={fetchSettings} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={saveSettings} disabled={saving} className="bg-green-600 hover:bg-green-700">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{productsWithDynamicPricing.length}</p>
                <p className="text-sm text-muted-foreground">Products with Price Increase</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100">
                <Flame className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {products?.filter(p => p.dynamic_pricing?.scarcity_label === "Last Few!")?.length || 0}
                </p>
                <p className="text-sm text-muted-foreground">Last Few! Items</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {products?.filter(p => ["Low Stock", "Almost Gone"].includes(p.dynamic_pricing?.scarcity_label))?.length || 0}
                </p>
                <p className="text-sm text-muted-foreground">Low Stock Items</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Percent className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {productsWithDynamicPricing.length > 0 
                    ? Math.round(productsWithDynamicPricing.reduce((sum, p) => sum + (p.dynamic_pricing?.increase_percent || 0), 0) / productsWithDynamicPricing.length)
                    : 0}%
                </p>
                <p className="text-sm text-muted-foreground">Avg Price Increase</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Pricing Tiers Configuration
          </CardTitle>
          <CardDescription>
            Configure how prices increase as stock decreases. Lower stock percentage triggers higher price multiplier.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-6">
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="font-medium">Enable Dynamic Pricing</p>
                <p className="text-sm text-muted-foreground">Automatically adjust prices based on stock levels</p>
              </div>
              <Switch 
                checked={settings.enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
              />
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Stock Level</TableHead>
                <TableHead>Price Multiplier</TableHead>
                <TableHead>Increase %</TableHead>
                <TableHead>Badge Label</TableHead>
                <TableHead>Example ($200 base)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {settings.tiers?.map((tier, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getScarcityIcon(tier.label)}
                      <span className="font-medium">≤ {tier.stock_percent}%</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      step="0.05"
                      min="1"
                      max="3"
                      value={tier.multiplier}
                      onChange={(e) => updateTier(index, "multiplier", parseFloat(e.target.value))}
                      className="w-24"
                    />
                  </TableCell>
                  <TableCell>
                    <Badge variant={tier.multiplier > 1 ? "default" : "secondary"}>
                      +{Math.round((tier.multiplier - 1) * 100)}%
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Input
                      type="text"
                      value={tier.label || ""}
                      onChange={(e) => updateTier(index, "label", e.target.value || null)}
                      placeholder="No label"
                      className="w-32"
                    />
                  </TableCell>
                  <TableCell className="font-medium">
                    {formatPrice(200 * tier.multiplier)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Products with Dynamic Pricing */}
      <Card>
        <CardHeader>
          <CardTitle>Products with Active Price Increases</CardTitle>
          <CardDescription>
            Products currently showing increased prices due to low stock
          </CardDescription>
        </CardHeader>
        <CardContent>
          {productsWithDynamicPricing.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No products currently have active price increases
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Stock %</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Base Price</TableHead>
                  <TableHead>Current Price</TableHead>
                  <TableHead>Increase</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {productsWithDynamicPricing.map((product) => (
                  <TableRow key={product.product_id}>
                    <TableCell className="font-medium">{product.title}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{product.category}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              product.dynamic_pricing.stock_percent <= 10 ? 'bg-red-500' :
                              product.dynamic_pricing.stock_percent <= 25 ? 'bg-amber-500' :
                              product.dynamic_pricing.stock_percent <= 50 ? 'bg-blue-500' :
                              'bg-green-500'
                            }`}
                            style={{ width: `${product.dynamic_pricing.stock_percent}%` }}
                          />
                        </div>
                        <span className="text-sm">{product.dynamic_pricing.stock_percent}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {product.dynamic_pricing.scarcity_label && (
                        <Badge className={`gap-1 ${
                          product.dynamic_pricing.scarcity_label === "Last Few!" ? "bg-red-500" :
                          product.dynamic_pricing.scarcity_label === "Almost Gone" ? "bg-orange-500" :
                          product.dynamic_pricing.scarcity_label === "Low Stock" ? "bg-amber-500" :
                          "bg-blue-500"
                        }`}>
                          {getScarcityIcon(product.dynamic_pricing.scarcity_label)}
                          {product.dynamic_pricing.scarcity_label}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="line-through text-muted-foreground">
                      {formatPrice(product.dynamic_pricing.base_price)}
                    </TableCell>
                    <TableCell className="font-bold text-green-600">
                      {formatPrice(product.dynamic_pricing.current_price)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="destructive">+{product.dynamic_pricing.increase_percent}%</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DynamicPricingTab;
